import { t } from '@glory/i18n';
import type { ProjectKind, ProjectStatus } from '@glory/projects/domain';
import {
  Button,
  Callout,
  cn,
  ContentSwap,
  Dialog,
  EmptyState,
  InfoHint,
  PageHeader,
  Segments,
  Select,
  SkeletonRows,
} from '@glory/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { FolderKanban, Plus } from 'lucide-react';

import { type ProjectsSearch, parseProjectsSearch } from '../../../features/projects/model/filters.ts';
import { kindOptions, statusOptions } from '../../../features/projects/model/labels.ts';
import { NewProjectDialog } from '../../../features/projects/ui/new-project-dialog.tsx';
import { PortfolioGantt } from '../../../features/projects/ui/portfolio-gantt.tsx';
import { ProjectTable } from '../../../features/projects/ui/project-table.tsx';
import { TeamLoadBoard } from '../../../features/projects/ui/team-load.tsx';
import { studioToday } from '../../../features/sales/model/dates.ts';
import { useCrumbs } from '../../../lib/crumbs.tsx';
import { orpc } from '../../../lib/orpc.ts';
import { requirePermissionRoute, usePermissions } from '../../../lib/permissions.ts';

export const Route = createFileRoute('/_app/projects/')({
  validateSearch: (search: Record<string, unknown>) => parseProjectsSearch(search),
  beforeLoad: requirePermissionRoute('projects.use'),
  component: ProjectsPage,
});

/**
 * «Проекты»: «Список · Гант · Загрузка». Список — таблица с фильтрами «Мои · Все», вид, состояние, «Архив»;
 * Гант — портфель этапами по времени; Загрузка — команда × недели (`projects.read_all`). Всё — в адресе.
 */
function ProjectsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { can } = usePermissions();
  const today = studioToday();
  const readAll = can('projects.read_all');
  const canCreate = can('projects.lead') || can('projects.manage');
  const view = search.view === 'load' && !readAll ? undefined : search.view;
  const scope = readAll ? (search.scope ?? 'mine') : 'mine';

  const setSearch = (patch: Partial<ProjectsSearch>) => {
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  };
  const viewLabel =
    view === 'gantt' ? t('projects.views.gantt') : view === 'load' ? t('projects.views.load') : null;
  useCrumbs(viewLabel === null ? null : [{ label: viewLabel }]);

  const list = useQuery({
    ...orpc.projects.list.queryOptions({
      input: {
        scope,
        archived: search.archived === true,
        ...(search.kind === undefined ? {} : { kind: search.kind }),
        ...(search.status === undefined ? {} : { status: search.status }),
      },
    }),
    placeholderData: keepPreviousData,
    enabled: view === undefined,
  });
  const portfolio = useQuery({
    ...orpc.projects.portfolio.queryOptions({ input: { scope } }),
    enabled: view === 'gantt',
  });

  const filtered = search.kind !== undefined || search.status !== undefined;
  const resetFilters = () => {
    setSearch({ kind: undefined, status: undefined });
  };
  const open = (id: string) => {
    void navigate({ to: '/projects/$id', params: { id } });
  };

  const scopeControl = readAll ? (
    <Segments
      size="sm"
      aria-label={t('projects.filters.scope')}
      value={scope}
      options={[
        { value: 'mine', label: t('projects.filters.mine') },
        { value: 'all', label: t('projects.filters.all') },
      ]}
      onValueChange={(next) => {
        setSearch({ scope: next === 'all' ? 'all' : undefined });
      }}
    />
  ) : null;

  let body;
  if (view === 'load') {
    body = <TeamLoadBoard today={today} />;
  } else if (view === 'gantt') {
    body = portfolio.isError ? (
      <Callout>{portfolio.error.message}</Callout>
    ) : portfolio.data === undefined ? (
      <SkeletonRows rows={6} />
    ) : portfolio.data.length === 0 ? (
      <EmptyState
        icon={<FolderKanban />}
        title={t('projects.portfolio.emptyTitle')}
        description={t('projects.portfolio.emptyText')}
      />
    ) : (
      <PortfolioGantt projects={portfolio.data} today={today} />
    );
  } else {
    const rows = list.data;
    body = list.isError ? (
      <Callout className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1">{list.error.message}</span>
        <Button size="sm" onClick={() => void list.refetch()}>
          {t('common.retry')}
        </Button>
      </Callout>
    ) : rows === undefined ? (
      <SkeletonRows rows={6} />
    ) : (
      <ProjectTable
        rows={rows}
        onOpen={(row) => {
          open(row.id);
        }}
        empty={
          filtered ? (
            <EmptyState
              icon={<FolderKanban />}
              title={t('projects.list.emptyFilteredTitle')}
              description={t('projects.list.emptyFilteredText')}
              action={<Button onClick={resetFilters}>{t('projects.filters.reset')}</Button>}
            />
          ) : search.archived === true ? (
            <EmptyState
              icon={<FolderKanban />}
              title={t('projects.list.emptyArchiveTitle')}
              description={t('projects.list.emptyArchiveText')}
            />
          ) : scope === 'mine' && readAll ? (
            <EmptyState
              icon={<FolderKanban />}
              title={t('projects.list.emptyMineTitle')}
              description={t('projects.list.emptyMineText')}
            />
          ) : (
            <EmptyState
              icon={<FolderKanban />}
              title={t('projects.list.emptyTitle')}
              description={t('projects.list.emptyText')}
              action={
                canCreate ? (
                  <Button
                    variant="primary"
                    onClick={() => {
                      setSearch({ create: true });
                    }}
                  >
                    <Plus /> {t('projects.newProject')}
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />
    );
  }

  return (
    <div className="flex min-h-full w-full flex-col gap-4 px-4 py-5 md:px-6">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            {t('projects.title')}
            <InfoHint hint={{ text: t('projects.hint'), example: t('projects.hintExample') }} />
          </span>
        }
        actions={
          canCreate ? (
            <Button
              variant="primary"
              onClick={() => {
                setSearch({ create: true });
              }}
            >
              <Plus /> {t('projects.newProject')}
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <Segments
          size="sm"
          aria-label={t('projects.views.label')}
          value={view ?? 'list'}
          options={[
            { value: 'list', label: t('projects.views.list') },
            { value: 'gantt', label: t('projects.views.gantt') },
            ...(readAll ? [{ value: 'load' as const, label: t('projects.views.load') }] : []),
          ]}
          onValueChange={(next) => {
            setSearch({ view: next === 'list' ? undefined : next });
          }}
        />
        {view === 'load' ? null : scopeControl}
        {view === undefined ? (
          <>
            <Select<ProjectKind>
              size="sm"
              aria-label={t('projects.filters.kind')}
              placeholder={t('projects.filters.kind')}
              className="w-full sm:w-40"
              value={search.kind ?? null}
              options={kindOptions()}
              clearable
              onValueChange={(kind) => {
                setSearch({ kind: kind ?? undefined });
              }}
            />
            <Select<ProjectStatus>
              size="sm"
              aria-label={t('projects.filters.status')}
              placeholder={t('projects.filters.status')}
              className="w-full sm:w-44"
              value={search.status ?? null}
              options={statusOptions()}
              clearable
              onValueChange={(status) => {
                setSearch({ status: status ?? undefined });
              }}
            />
            <button
              type="button"
              aria-pressed={search.archived === true}
              onClick={() => {
                setSearch({ archived: search.archived === true ? undefined : true });
              }}
              className={cn(
                'rounded-full border-thick px-2.5 py-0.5 text-small font-semibold transition-colors',
                search.archived === true
                  ? 'border-strong bg-strong text-on-strong'
                  : 'border-border text-muted hover:text-text',
              )}
            >
              {t('projects.filters.archived')}
            </button>
          </>
        ) : null}
      </div>

      <ContentSwap swapKey={view ?? 'list'}>{body}</ContentSwap>

      <Dialog
        open={search.create === true && canCreate}
        onOpenChange={(next) => {
          if (!next) setSearch({ create: undefined });
        }}
      >
        {search.create === true && canCreate ? (
          <NewProjectDialog
            canPickLead={can('projects.manage')}
            onDone={(project) => {
              open(project.id);
            }}
          />
        ) : null}
      </Dialog>
    </div>
  );
}
